{**********************************************************************************
 * Copyright (C) 1990-2024 Systematic Marketing Ltd (SMXi Software)
 *
 * All code contained within this file is copyrighted and may only be used by
 * third parties with written permission from Systematic Marketing Ltd.
 *
 * For permissions, please contact us at: info@smxi.com
 * Visit our website for more information: https://smxi.com/codecopyright.html
 **********************************************************************************}

unit SMX.Web.Document.Utils;

interface

uses
  System.Classes,
  System.SysUtils,
  JS;

type

  TValidityState = (vsValid, vsInvalid, vsVoid, vsNone);

  TInputType = (witButton, witCheckbox, witColor, witDate, witDatetimeLocal, witEmail, witFile, witHidden, witImage,
    witMonth, witNumber, witPassword, witRadio, witRange, witReset, witSearch, witSubmit, witTel, witText, witTime,
    witUrl, witWeek);

  TDocUtils = class
  public

    class function stringsToUL(AStrings: TStrings; const AListClass: string = ''; AItemClass: string = ''): string;

    class function elementIdExists(const aElementId: string): Boolean;

    class procedure addClass(const aElementId: string; const AClassName: string);
    class procedure removeClass(const aElementId: string; const AClassName: string);
    class procedure setControlValidity(const aElementId: string; const aState: TValidityState);
    class procedure HighlightControl(const ThisElementId, TargetElementId: string; const AValid, Highlight: Boolean;
      const UseAltScheme: Boolean = False);

    class procedure hideElement(const aElementId: string);
    class procedure showElement(const aElementId: string);
    class procedure ShowHideElement(const aElementId: string; aConditionToShow: Boolean);
    class procedure ShowHideLabel(const aElementId: string; aConditionToShow: Boolean);

    class procedure hideElementWithClass(const aElementId: string);
    class procedure showElementWithClass(const aElementId: string);

    class procedure focusElement(const aElementId: string);

    class function elementHeight(const aElementId: string): integer;
    class function elementWidth(const aElementId: string): integer;
    class procedure writeElementText(const aElementId, aText: string);
    class procedure writeHTML(const aElementId: string; const Value: string);
    class procedure loadHTML(const aElementId: string; const URL: string);
    class procedure appendHTML(const aElementId: string; const Value: string);
    class procedure emptyDiv(const aElementId: string);
    class procedure setInputType(const aElementId: string; const aInputType: TInputType);
    class procedure replaceInnerText(const aElementId: string; const Value: string);

    class function isCSSLinked(const aFileURL: string): Boolean;
    /// <summary>
    /// The TMS WebCore Application object now has InsertCSS and RemoveCSS,
    /// so probably better to move to that
    /// </summary>
    class procedure addCSSFile(const aFileURL: string);

    class function isScriptLinked(const aFileURL: string): Boolean;
    class procedure addScriptFile(const aFileURL: string);

    class procedure writeImageSrc(const aElementId, aImageURL: string);
    class procedure pushState(const AURL: string; const ATitle: string = 'unused'; const aState: string = '{}');
    class procedure replaceState(const AURL: string; const ATitle: string = 'unused'; const aState: string = '{}');

    [async]
    /// <summary>
    /// usage: await(UInt32, Sleep(100))
    /// </summary>
    class function Sleep(const milliseconds: UInt32): TJSPromise; async;
  end;

const
  Valid_Check: array[Boolean] of TValidityState = (vsInvalid, vsValid);
  Alt_Valid_Check: array[Boolean] of TValidityState = (vsInvalid, vsNone);
  smx_hide_class = 'smxhide';
  smx_show_class = 'smxshow';

implementation

uses
  System.Rtti,
  Web,
  WebLib.Forms;

const

  validity_class_map: array[TValidityState] of string = ('is-valid', 'is-invalid', 'is-void', 'is-none');

  input_type_map: array[TInputType] of string = ('button', 'checkbox', 'color', 'date', 'datetime - local', 'email',
    'file', 'hidden', 'image', 'month', 'number', 'password', 'radio', 'range', 'reset', 'search', 'submit', 'tel',
    'text', 'time', 'url', 'week');

  { THTMLHelper }

  {$HINTS OFF}

class procedure TDocUtils.addClass(const aElementId, AClassName: string);
begin
  {$IFDEF PAS2JS}
  asm
    $("#" + aElementId).addClass(AClassName);
  end;
  {$ENDIF}
end;

class procedure TDocUtils.addCSSFile(const aFileURL: string);
begin
  if isCSSLinked(aFileURL) then
    Exit;

  {$IFDEF PAS2JS}
  asm
    var link = document.createElement('link');

    // set the attributes for link element
    link.rel = 'stylesheet';

    link.type = 'text/css';

    link.href = aFileURL;

    // Get HTML head element to append
    // link element to it
    document.getElementsByTagName('HEAD')[0].appendChild(link);
  end;
  {$ENDIF}
end;

class procedure TDocUtils.addScriptFile(const aFileURL: string);
begin
  if isScriptLinked(aFileURL) then
    Exit;

end;

class procedure TDocUtils.appendHTML(const aElementId, Value: string);
begin
  {$IFDEF PAS2JS}
  asm
    var Doc = document.getElementById(aElementId);
    if (Doc !== null) {
    Doc.innerHTML += Value;
     }
  end;
  {$ENDIF}
end;

class function TDocUtils.elementHeight(const aElementId: string): integer;
begin
  {$IFDEF PAS2JS}
  asm
    Result = parseInt($("#" + aElementId).height());
  end;
  {$ENDIF}
end;

class function TDocUtils.elementIdExists(const aElementId: string): Boolean;
begin
  {$IFDEF PAS2JS}
  asm
    return (document.getElementById(aElementId) !== null);
  end;
  {$ENDIF}
end;

class function TDocUtils.elementWidth(const aElementId: string): integer;
begin
  {$IFDEF PAS2JS}
  asm
    Result = parseInt($("#" + aElementId).width());
  end;
  {$ENDIF}
end;

class procedure TDocUtils.emptyDiv(const aElementId: string);
begin
  {$IFDEF PAS2JS}
  asm
    var Doc = document.getElementById(aElementId);
    if (Doc !== null) {
    Doc.innerHTML = "";
     }
  end;
  {$ENDIF}
end;

class procedure TDocUtils.focusElement(const aElementId: string);
begin
  {$IFDEF PAS2JS}
  asm
   $('#' + aElementId).focus();
  end;
  {$ENDIF}
end;

class procedure TDocUtils.hideElement(const aElementId: string);
begin
  {$IFDEF PAS2JS}
  asm
    $("#" + aElementId).hide();
  end;
  {$ENDIF}
end;

class procedure TDocUtils.hideElementWithClass(const aElementId: string);
begin
  addClass(aElementId, smx_hide_class);
end;

class procedure TDocUtils.HighlightControl(const ThisElementId,
  TargetElementId: string; const AValid, Highlight, UseAltScheme: Boolean);
var
  lValidityState: TValidityState;
begin
  if Highlight or (ThisElementId = TargetElementId) then
  begin
    if UseAltScheme then
      lValidityState := Alt_Valid_Check[AValid]
    else
      lValidityState := Valid_Check[AValid];
    setControlValidity(ThisElementId, lValidityState);
  end;
end;

class function TDocUtils.isCSSLinked(const aFileURL: string): Boolean;
begin
  {$IFDEF PAS2JS}
  asm
    var linkEl = document.head.querySelector('link[href*="' + aFileURL + '"]');
    return Boolean(linkEl.sheet);
  end;
  {$ENDIF}
end;

class function TDocUtils.isScriptLinked(const aFileURL: string): Boolean;
begin
  {$IFDEF PAS2JS}
  asm
    const found_in_resources = performance.getEntries()
    .filter(e => e.entryType === 'resource')
    .map(e => e.name)
    .indexOf(src) !== -1;
    const found_in_script_tags = document.querySelectorAll(`script[src*="${ src }"]`).length > 0;
    return found_in_resources || found_in_script_tags;
  end;
  {$ENDIF}
end;

class procedure TDocUtils.loadHTML(const aElementId, URL: string);
begin
  {$IFDEF PAS2JS}
  asm
    $("#" + aElementId).load(URL);
  end;
  {$ENDIF}
end;

class procedure TDocUtils.pushState(const AURL: string; const ATitle: string = 'unused'; const aState: string = '{}');
begin
  {$IFDEF PAS2JS}
  asm
    history.pushState(aState, ATitle, AURL);
  end;
  {$ENDIF}
end;

class procedure TDocUtils.removeClass(const aElementId, AClassName: string);
begin
  {$IFDEF PAS2JS}
  asm
    $("#" + aElementId).removeClass(AClassName);
  end;
  {$ENDIF}
end;

class procedure TDocUtils.replaceInnerText(const aElementId, Value: string);
begin
  {$IFDEF PAS2JS}
  asm
    let span = document.getElementById(aElementId);
    span.innerText = Value;
  end;
  {$ENDIF}
end;

class procedure TDocUtils.replaceState(const AURL: string; const ATitle: string = 'unused';
  const aState: string = '{}');
begin
  {$IFDEF PAS2JS}
  asm
    history.replaceState(aState, ATitle, AURL);
  end;
  {$ENDIF}
end;

class procedure TDocUtils.setControlValidity(const aElementId: string; const aState: TValidityState);
var
  lState: TValidityState;
begin
  for lState := low(TValidityState) to high(TValidityState) do
  begin
    if lState <> aState then
      removeClass(aElementId, validity_class_map[lState]);
  end;

  if aState <> TValidityState.vsNone then
    addClass(aElementId, validity_class_map[aState]);

end;

class procedure TDocUtils.setInputType(const aElementId: string; const aInputType: TInputType);
var
  lInputType: string;
begin
  lInputType := input_type_map[aInputType];
  {$IFDEF PAS2JS}
  asm
    const element = document.getElementById(aElementId);
    if (element.tagName.toLowerCase() === 'input') {
    Document.getElementById(aElementId).type = lInputType;
     };
  end;
  {$ENDIF}
end;

class procedure TDocUtils.showElement(const aElementId: string);
begin
  {$IFDEF PAS2JS}
  asm
    $("#" + aElementId).show();
  end;
  {$ENDIF}
end;

class procedure TDocUtils.showElementWithClass(const aElementId: string);
begin
  removeClass(aElementId, smx_hide_class);
end;

class procedure TDocUtils.ShowHideElement(const aElementId: string;
  aConditionToShow: Boolean);
begin
  if aConditionToShow then
    showElement(aElementId)
  else
    hideElement(aElementId);
end;

class procedure TDocUtils.ShowHideLabel(const aElementId: string;
  aConditionToShow: Boolean);
begin
  {$IFDEF PAS2JS}
  asm
    var el = document.getElementById(aElementId);
    if (el !== null) {
      if (aConditionToShow) {
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    }
  end;
  {$ENDIF}
end;

class function TDocUtils.Sleep(const milliseconds: UInt32): TJSPromise;
begin
  Result := TJSPromise.New(
    procedure(success, _: TJSPromiseResolver)
    begin
      window.setTimeout(
        procedure
        begin
          success(milliseconds);
        end,
        milliseconds
          );
    end);
end;

class function TDocUtils.stringsToUL(AStrings: TStrings; const AListClass: string = '';
  AItemClass: string = ''): string;
var
  I: integer;
  lClass: string;
begin
  Result := '';

  if AItemClass <> '' then
    lClass := format(' class="%s">', [AItemClass])
  else
    lClass := '>';

  for I := 0 to AStrings.Count - 1 do
    Result := Result + '<li' + lClass + AStrings[I] + '</li>';

  if AListClass <> '' then
    lClass := format(' class="%s">', [AListClass])
  else
    lClass := '>';

  Result := '<ul' + lClass + Result + '</ul>';
end;

class procedure TDocUtils.writeElementText(const aElementId, aText: string);
begin
  {$IFDEF PAS2JS}
  asm
    document.getElementById(aElementId).textContent = aText;
  end;
  {$ENDIF}

end;

class procedure TDocUtils.writeHTML(const aElementId, Value: string);
begin
  {$IFDEF PAS2JS}
  asm
    $("#" + aElementId).html(Value);
  end;
  {$ENDIF}
end;

class procedure TDocUtils.writeImageSrc(const aElementId, aImageURL: string);
begin
  {$IFDEF PAS2JS}
  asm
    var img
    $("#" + aElementId).attr("src", aImageURL);
  end;
  {$ENDIF}
end;

{$HINTS ON}

end.

